import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { ApiError } from '@/api/client';
import { discard, retryFailed } from './queue';
import { useUploadQueue } from './useUploadQueue';
import type { QueueItem } from './types';

const KIND_LABEL: Record<QueueItem['kind'], string> = {
  PUNCH: 'queue.kindPunch',
  HIDDEN_WORK: 'queue.kindHiddenWork',
  DAILY_LOG: 'queue.kindDailyLog',
  PURCHASE: 'queue.kindPurchase',
};

/**
 * 待上传状态条。
 *
 * 工人最怕的是「我明明拍了，系统里却没有」。所以队列必须是**可见的**：
 * 有多少项没传、卡在哪、为什么失败，都要能点开看到。
 * 一个只在后台默默重试的队列，出问题时工人只会觉得这个 App 不可信。
 */
export function QueueBar() {
  const { t } = useTranslation();
  const queue = useUploadQueue();
  const [expanded, setExpanded] = useState(false);
  const toMessage = useErrorMessage();

  const outstanding = queue.pending + queue.uploading + queue.failed;
  if (outstanding === 0) return null;

  const hasFailure = queue.failed > 0;

  return (
    <div
      className={[
        'rounded-md border px-3 py-2.5 text-body',
        hasFailure
          ? 'border-danger/40 bg-danger/8 text-danger'
          : 'border-safety-500/40 bg-safety-50 text-safety-600',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <span className="font-medium">
          {hasFailure
            ? t('queue.failedSummary', { count: queue.failed })
            : t('queue.pendingSummary', {
                count: outstanding,
                files: queue.pendingFiles,
              })}
        </span>
        <span className="shrink-0 text-caption opacity-70">
          {expanded ? t('queue.collapse') : t('queue.expand')}
        </span>
      </button>

      {expanded && (
        <ul className="mt-3 flex flex-col gap-2 border-t border-current/15 pt-3">
          {queue.items.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{t(KIND_LABEL[item.kind])}</p>
                <p className="text-caption opacity-80">
                  {new Date(item.createdAt).toLocaleString()}
                  {item.status === 'UPLOADING' && ` · ${t('queue.uploading')}`}
                  {item.status === 'PENDING' && item.attempts > 0 && (
                    ` · ${t('queue.retryCount', { count: item.attempts })}`
                  )}
                </p>
                {item.status === 'FAILED' && item.lastError && (
                  <p className="mt-0.5 text-caption">
                    {toMessage(new ApiError(item.lastError as never, 0))}
                  </p>
                )}
              </div>

              {item.status === 'FAILED' && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(t('queue.discardConfirm'))) void discard(item.id);
                  }}
                  className="shrink-0 text-caption underline opacity-80 hover:opacity-100"
                >
                  {t('queue.discard')}
                </button>
              )}
            </li>
          ))}

          {hasFailure && (
            <li className="pt-1">
              <Button variant="secondary" onClick={() => void retryFailed()}>
                {t('queue.retryAll')}
              </Button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
